import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { getCustomerSiteUrl } from '@/lib/domain'

import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Copy, Check, ExternalLink } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

// Read by the viewer: the embed page (appearance + enabled) and the
// middleware (allowed domains → frame-ancestors header). booking_ prefix is
// deliberate — it's already anon-readable under the viewer's RLS policy.
const EMBED_SETTINGS = {
  ENABLED: 'booking_embed_enabled',
  ALLOWED_DOMAINS: 'booking_embed_allowed_domains',
  BUTTON_TEXT: 'booking_embed_button_text',
  ACCENT_COLOR: 'booking_embed_accent_color',
  LAYOUT: 'booking_embed_layout',
  TRANSPARENT_BG: 'booking_embed_transparent_bg',
} as const

const SETTING_DESCRIPTIONS: Record<string, string> = {
  [EMBED_SETTINGS.ENABLED]: 'Enable the embeddable booking widget (third-party iframe)',
  [EMBED_SETTINGS.ALLOWED_DOMAINS]:
    'Domains allowed to embed the booking widget (one per line, enforced via frame-ancestors)',
  [EMBED_SETTINGS.BUTTON_TEXT]: 'Submit button text on the embedded booking widget',
  [EMBED_SETTINGS.ACCENT_COLOR]: 'Accent color (hex) for the embedded booking widget',
  [EMBED_SETTINGS.LAYOUT]: 'Embedded widget layout: card or full_width',
  [EMBED_SETTINGS.TRANSPARENT_BG]: 'Transparent background behind the embedded widget',
}

/**
 * Strip scheme/path/port-noise from whatever the operator pasted and keep a
 * bare hostname (a :port is kept — needed for local testing). Returns null
 * for lines that can't be salvaged into a hostname.
 */
function normalizeDomain(raw: string): string | null {
  let value = raw.trim().toLowerCase()
  if (!value) return null
  value = value.replace(/^[a-z]+:\/\//, '') // scheme
  value = value.split('/')[0] // path
  if (!value || !/^[a-z0-9*][a-z0-9.:*-]*$/.test(value)) return null
  return value
}

const embedWidgetSchema = z.object({
  enabled: z.boolean().default(false),
  allowed_domains: z.string().default(''),
  button_text: z.string().max(40, 'Keep it under 40 characters').default(''),
  accent_color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex color like #1f7a99')
    .or(z.literal(''))
    .default(''),
  layout: z.enum(['card', 'full_width']).default('card'),
  transparent_bg: z.boolean().default(false),
})

type EmbedWidgetFormData = z.infer<typeof embedWidgetSchema>

/**
 * Embed Widget settings: lets an operator drop the booking form onto their
 * own marketing site. Generates the copy-paste snippet (pointing at the
 * account's booking domain), manages the domain allowlist that gates framing,
 * and the widget's appearance.
 */
export function EmbedWidgetSettingsPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Same query key as the other Booking tabs so they share one cache entry
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  const form = useForm<EmbedWidgetFormData>({
    resolver: zodResolver(embedWidgetSchema),
    defaultValues: {
      enabled: false,
      allowed_domains: '',
      button_text: '',
      accent_color: '',
      layout: 'card',
      transparent_bg: false,
    },
  })

  useEffect(() => {
    if (settings) {
      form.reset({
        enabled: settings[EMBED_SETTINGS.ENABLED] === 'true',
        allowed_domains: settings[EMBED_SETTINGS.ALLOWED_DOMAINS] || '',
        button_text: settings[EMBED_SETTINGS.BUTTON_TEXT] || '',
        accent_color: settings[EMBED_SETTINGS.ACCENT_COLOR] || '',
        layout:
          settings[EMBED_SETTINGS.LAYOUT] === 'full_width' ? 'full_width' : 'card',
        transparent_bg: settings[EMBED_SETTINGS.TRANSPARENT_BG] === 'true',
      })
    }
  }, [settings, form])

  const mutation = useMutation({
    mutationFn: async (data: EmbedWidgetFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const normalizedDomains = data.allowed_domains
        .split(/[\n,]/)
        .map(normalizeDomain)
        .filter((d): d is string => Boolean(d))
      // De-dupe while preserving order
      const uniqueDomains = [...new Set(normalizedDomains)]

      const rows = [
        { key: EMBED_SETTINGS.ENABLED, value: String(data.enabled) },
        { key: EMBED_SETTINGS.ALLOWED_DOMAINS, value: uniqueDomains.join('\n') },
        { key: EMBED_SETTINGS.BUTTON_TEXT, value: data.button_text.trim() },
        { key: EMBED_SETTINGS.ACCENT_COLOR, value: data.accent_color.trim() },
        { key: EMBED_SETTINGS.LAYOUT, value: data.layout },
        { key: EMBED_SETTINGS.TRANSPARENT_BG, value: String(data.transparent_bg) },
      ].map((row) => ({
        account_id: activeAccount.id,
        key: row.key,
        value: row.value,
        description: SETTING_DESCRIPTIONS[row.key],
        is_secret: false,
      }))

      const { error } = await supabase
        .from('account_settings')
        .upsert(rows, { onConflict: 'account_id,key' })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Embed widget settings saved',
        description:
          'Changes to allowed domains can take up to 5 minutes to apply on embedded sites.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const baseUrl = useMemo(
    () => (activeAccount ? getCustomerSiteUrl(activeAccount) : ''),
    [activeAccount]
  )

  const snippet = useMemo(
    () =>
      [
        '<!-- Booked Rides booking widget -->',
        '<div id="limo-booking-widget"></div>',
        `<script src="${baseUrl}/api/widget" data-limo-booking async></script>`,
      ].join('\n'),
    [baseUrl]
  )

  const handleCopySnippet = () => {
    navigator.clipboard.writeText(snippet)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast({ title: 'Snippet copied to clipboard' })
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Embed Widget</CardTitle>
          <CardDescription>
            Put your booking form on any website you own. Visitors fill out the
            trip details there and are brought to your booking site to pick a
            vehicle and pay.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
              className="space-y-6"
            >
              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable embed widget</FormLabel>
                      <FormDescription>
                        Master switch. While off, the widget shows a &quot;booking
                        not available&quot; message on every site that embeds it.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="allowed_domains"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Allowed website domains</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder={'azlimo.com\nwww.azlimo.com\npromo.azlimo.com'}
                      />
                    </FormControl>
                    <FormDescription>
                      One domain per line. The widget will not load on a website
                      until its domain is listed here (www. is included
                      automatically for bare domains). Changes can take up to 5
                      minutes to apply.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-6 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="button_text"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Button text</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Get My Quote" />
                      </FormControl>
                      <FormDescription>
                        Leave blank to use &quot;Get My Quote&quot;.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accent_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accent color</FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Input {...field} placeholder="#1f7a99" className="flex-1" />
                        </FormControl>
                        <input
                          type="color"
                          aria-label="Pick accent color"
                          value={
                            /^#[0-9a-fA-F]{6}$/.test(field.value)
                              ? field.value
                              : '#1f7a99'
                          }
                          onChange={(e) => field.onChange(e.target.value)}
                          className="h-9 w-12 cursor-pointer rounded border p-1"
                        />
                      </div>
                      <FormDescription>
                        Buttons, tabs and focus states. Leave blank for the default.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="layout"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Layout</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="card">Card (centered, max 500px)</SelectItem>
                          <SelectItem value="full_width">
                            Full width (fills its container)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="transparent_bg"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel>Transparent background</FormLabel>
                        <FormDescription>
                          Let your page show through around the form.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Install on your website</CardTitle>
          <CardDescription>
            Paste this snippet where the booking form should appear. The
            placeholder <code>&lt;div&gt;</code> controls the position — your
            page&apos;s own layout controls the width, and the widget manages its
            own height.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <pre className="overflow-x-auto rounded-lg border bg-muted p-4 text-xs leading-relaxed">
            <code>{snippet}</code>
          </pre>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" onClick={handleCopySnippet}>
              {copied ? (
                <Check className="mr-2 h-4 w-4" />
              ) : (
                <Copy className="mr-2 h-4 w-4" />
              )}
              {copied ? 'Copied' : 'Copy snippet'}
            </Button>
            <Button type="button" variant="ghost" asChild>
              <a
                href={`${baseUrl}/embed/booking-form`}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Preview the widget
              </a>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Tip: on page builders where you can&apos;t place the div, add{' '}
            <code>data-target=&quot;.your-css-selector&quot;</code> to the script
            tag, or paste the script alone — the form renders right where the
            script sits.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
